import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

const AgeCalculator = () => {
  const [birthDate, setBirthDate] = useState('');
  const [compareDate, setCompareDate] = useState(new Date().toISOString().split('T')[0]);
  const [result, setResult] = useState(null);

  const calculateAge = () => {
    if (!birthDate) return;

    const birth = new Date(birthDate);
    const compare = new Date(compareDate);

    if (birth > compare) {
      setResult({ error: 'Geburtsdatum darf nicht in der Zukunft liegen.' });
      return;
    }

    // Calculate exact age
    let years = compare.getFullYear() - birth.getFullYear();
    let months = compare.getMonth() - birth.getMonth();
    let days = compare.getDate() - birth.getDate();

    if (days < 0) {
      months--;
      const lastMonth = new Date(compare.getFullYear(), compare.getMonth(), 0);
      days += lastMonth.getDate();
    }

    if (months < 0) {
      years--;
      months += 12;
    }

    // Calculate total values
    const totalDays = Math.floor((compare - birth) / (1000 * 60 * 60 * 24));
    const totalWeeks = Math.floor(totalDays / 7);
    const remainingDays = totalDays % 7;
    const totalMonths = years * 12 + months;

    setResult({
      years,
      months,
      days,
      totalDays,
      totalWeeks,
      remainingDays,
      totalMonths
    });
  };

  useEffect(() => {
    calculateAge();
  }, [birthDate, compareDate]);

  return (
    <div className="space-y-6">
      {/* Input Fields */}
      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <label className="block text-white font-medium mb-2">
            Geburtsdatum
          </label>
          <input
            type="date"
            value={birthDate}
            onChange={(e) => setBirthDate(e.target.value)}
            className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-purple-400"
          />
        </div>
        <div>
          <label className="block text-white font-medium mb-2">
            Vergleichsdatum
          </label>
          <input
            type="date"
            value={compareDate}
            onChange={(e) => setCompareDate(e.target.value)}
            className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-purple-400"
          />
        </div>
      </div>

      {/* Results */}
      {result && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/5 rounded-xl p-6 border border-white/10"
        >
          {result.error ? (
            <p className="text-red-400 text-center">{result.error}</p>
          ) : (
            <div className="space-y-4">
              <h3 className="text-2xl font-bold text-white text-center mb-6">
                Ihr Alter
              </h3>
              
              <div className="grid md:grid-cols-3 gap-4 mb-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-purple-300">{result.years}</div>
                  <div className="text-white/70">Jahre</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-purple-300">{result.months}</div>
                  <div className="text-white/70">Monate</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-purple-300">{result.days}</div>
                  <div className="text-white/70">Tage</div>
                </div>
              </div>

              <div className="border-t border-white/10 pt-4">
                <h4 className="text-lg font-semibold text-white mb-3">Zusätzliche Informationen</h4>
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div className="flex justify-between">
                    <span className="text-white/70">Gesamttage:</span>
                    <span className="text-white font-medium">{result.totalDays.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/70">Gesamtwochen:</span>
                    <span className="text-white font-medium">{result.totalWeeks.toLocaleString()} ({result.remainingDays} Resttage)</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/70">Gesamtmonate:</span>
                    <span className="text-white font-medium">{result.totalMonths}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
};

export default AgeCalculator;